/**
 * Outbound-email abuse-prevention domain enums. Shared by the `outbound_email`
 * outbox, the `email_sending_restriction` records, and the `OutboundEmailPolicy`.
 */

export enum OutboundEmailFeature {
    invoice = "invoice",
}

/**
 * Why an outbound email was sent: first send by a user, a scheduled fire, a
 * follow-up resend, or a test copy to the actor. Stored on the `outbound_email`
 * row (nullable: rows written before this column existed have no reason).
 */
export enum OutboundEmailReason {
    user = "user",
    scheduled = "scheduled",
    resend = "resend",
    test = "test",
}

export const OUTBOUND_EMAIL_INITIAL_SEND_REASONS: readonly OutboundEmailReason[] =
    [OutboundEmailReason.user, OutboundEmailReason.scheduled];

/**
 * Reputation split: Kick-owned SendGrid IPs (default `invoices@kick.co` and DNS
 * whitelabel) vs. the customer's own SMTP or Gmail. Only Kick-IP mail is gated.
 */
export enum OutboundEmailTransport {
    kick_sendgrid = "kick_sendgrid",
    customer_smtp = "customer_smtp",
    customer_gmail = "customer_gmail",
}

export function isCustomerOwnedOutboundTransport(
    transport: OutboundEmailTransport,
): boolean {
    return (
        transport === OutboundEmailTransport.customer_smtp ||
        transport === OutboundEmailTransport.customer_gmail
    );
}

export enum OutboundEmailStatus {
    pending = "pending",
    sent = "sent",
    blocked = "blocked",
    rate_limited = "rate_limited",
    failed = "failed",
}

export const OUTBOUND_EMAIL_UNDELIVERED_STATUSES: readonly OutboundEmailStatus[] =
    [
        OutboundEmailStatus.blocked,
        OutboundEmailStatus.rate_limited,
        OutboundEmailStatus.failed,
    ];

export enum OutboundEmailBlockReason {
    rate_limited = "rate_limited",
    link_rejected = "link_rejected",
    keyword_rejected = "keyword_rejected",
    sending_disabled = "sending_disabled",
    policy_unavailable = "policy_unavailable",
    from_unresolved = "from_unresolved",
}

export enum EmailSendingRestrictionOwnerType {
    workspace = "workspace",
    organization = "organization",
}

export enum EmailSendingRestrictionKind {
    cooldown = "cooldown",
    disabled = "disabled",
}

/** v1 only restricts Kick-owned SendGrid IP sending. */
export enum EmailSendingTransportScope {
    kick_sendgrid = "kick_sendgrid",
}

export enum EmailSendingRestrictionCreatedBy {
    policy = "policy",
    admin = "admin",
}

/** Latest outbox attempt surfaced on invoice send/resend/schedule responses. */
export interface LastOutboundEmail {
    id: string;
    status: OutboundEmailStatus;
    blockReason: OutboundEmailBlockReason | null;
    createdAt: string;
}
